import React, { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { useLiveVideo } from '../../../hooks/useLiveVideo';
import { useUpcomingLives } from '../../../hooks/useUpcomingLives';
import { useDirection } from '../../../contexts/DirectionContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { VideoContent } from '../../../types/content';
import { formatStreamingDuration } from '../../../utils/formatters';
import { normalizeLanguageCode } from '../../../utils/platform';
import { Countdown } from '../Countdown';
import NotificationService from '../../../services/notifications';

const LiveBanner: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { isRTL } = useDirection();
    const { theme } = useTheme();
    const bannerRef = useRef<HTMLDivElement>(null);
    const { liveVideo, loading: liveLoading } = useLiveVideo();
    const { videos: upcomingVideos, loading: upcomingLoading } = useUpcomingLives({
        limit: 1,
        sortBy: 'scheduledStartTime',
        sortType: 'asc',
    });

    const [reminderId, setReminderId] = useState<string | null>(null);
    const [hasReminder, setHasReminder] = useState(false);

    const nextVideo = useMemo(() => {
        if (liveLoading || upcomingLoading) return null;
        if (liveVideo) return liveVideo;

        const nextUpcoming = upcomingVideos[0];
        if (!nextUpcoming || !nextUpcoming.scheduledStartTime) return null;

        const startTime = new Date(nextUpcoming.scheduledStartTime);
        const now = new Date();
        const hoursDifference = (startTime.getTime() - now.getTime()) / (1000 * 60 * 60);

        return hoursDifference <= 24 ? nextUpcoming : null;
    }, [liveVideo, liveLoading, upcomingVideos, upcomingLoading]);

    const formatTime = useCallback((date: Date) => {
        return date.toLocaleTimeString(i18n.language, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: !isRTL
        });
    }, [i18n.language, isRTL]);

    const streamingDuration = useMemo(() => {
        if (!nextVideo || !nextVideo.scheduledStartTime || nextVideo.broadcastStatus !== 'live') return null;
        const startTime = new Date(nextVideo.scheduledStartTime);
        const now = new Date();
        const diffMs = now.getTime() - startTime.getTime();
        return formatStreamingDuration(diffMs, t, normalizeLanguageCode(i18n.language));
    }, [nextVideo, t, i18n.language]);

    // Check for existing reminder on mount and when nextVideo changes
    useEffect(() => {
        if (nextVideo?.scheduledStartTime) {
            const startTime = new Date(nextVideo.scheduledStartTime);
            const hasActive = NotificationService.hasActiveReminder(startTime);
            setHasReminder(hasActive);
        }
    }, [nextVideo]);

    // Cleanup reminder when component unmounts
    useEffect(() => {
        return () => {
            if (reminderId) {
                NotificationService.cancelReminder(reminderId);
            }
        };
    }, [reminderId]);

    const handleSetReminder = useCallback(async (e: React.MouseEvent, video: VideoContent) => {
        e.preventDefault();
        
        if (!video.scheduledStartTime) return;
        
        try {
            const startTime = new Date(video.scheduledStartTime);
            
            if (hasReminder && reminderId) {
                // Cancel existing reminder
                NotificationService.cancelReminder(reminderId);
                setReminderId(null);
                setHasReminder(false);
                return;
            }

            const newReminderId = await NotificationService.scheduleReminder({
                title: t('lives.startingSoon'),
                body: video.title,
                icon: video.thumbnail,
                timestamp: startTime,
                reminderMinutes: 5
            });

            if (newReminderId) {
                setReminderId(newReminderId);
                setHasReminder(true);
            }
        } catch (error) {
            console.error('Failed to set reminder:', error);
        }
    }, [t, hasReminder, reminderId]);

    useEffect(() => {
        const root = document.documentElement;
        const bannerHeight = bannerRef.current?.offsetHeight || 0;
        const headerHeight = parseInt(getComputedStyle(root).getPropertyValue('--header-height'), 10) || 64;
        root.style.setProperty('--header-total-height', `${headerHeight + bannerHeight}px`);

        return () => {
            root.style.setProperty('--header-total-height', `${headerHeight}px`);
        };
    }, [nextVideo]);

    if (!nextVideo || !nextVideo.scheduledStartTime) {
        return null;
    }

    const isLive = nextVideo.broadcastStatus === 'live';
    const startTime = new Date(nextVideo.scheduledStartTime);

    return (
        <div
            ref={bannerRef}
            className={`
        w-full 
        ${isLive ? 'bg-status-error' : 'bg-status-warning'}
        ${theme.mode === 'dark' ? 'bg-opacity-90' : 'bg-opacity-95'}
        backdrop-blur-sm
        transition-all duration-200
        cursor-pointer
      `}
        >
            <Link to="/live" className="block">
                <div className={`
                    container mx-auto 
                    flex items-center
                    py-1.5 px-4
                    text-sm
                    gap-4
                `}>
                    {/* Content */}
                    <div className={`
                        flex items-center gap-2 overflow-hidden
                        flex-1 min-w-0
                    `}>
                        {isLive ? (
                            <div className={`
                                inline-flex items-center flex-shrink-0 gap-2
                                font-medium text-white
                            `}>
                                <div className={`
                                    flex items-center gap-2
                                `}>
                                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse flex-shrink-0" />
                                    <span className="uppercase tracking-wider whitespace-nowrap">{t('liveBanner.liveNow')}:</span>
                                </div>
                                <span className={`
                                    text-white/80 whitespace-nowrap
                                `}>
                                    {t('liveBanner.startedAt')} {formatTime(startTime)}
                                    {streamingDuration && ` • ${t('liveBanner.streamingFor')} ${streamingDuration}`}
                                </span>
                            </div>
                        ) : (
                            <div className={`
                                inline-flex items-center flex-shrink-0 gap-2
                                font-medium text-white
                            `}>
                                <div className={`
                                    flex items-center gap-2
                                `}>
                                    <Icon icon="ph:clock-thin" className="w-4 h-4 flex-shrink-0" />
                                    <span className="uppercase tracking-wider whitespace-nowrap">{t('liveBanner.starting')}:</span>
                                </div>
                                <span className="text-white/80 whitespace-nowrap">
                                    <Countdown targetDate={startTime} className="font-medium text-white" />
                                </span>
                            </div>
                        )}
                        <span className="font-medium truncate text-white">
                            {nextVideo.title}
                        </span>
                    </div>

                    {/* Action Button - Positioned based on RTL */}
                    {isLive ? (
                        <div
                            onClick={(e) => e.preventDefault()}
                            className={`
                                flex-shrink-0
                                inline-flex items-center gap-2
                                px-3 py-0.5 rounded-full
                                font-medium
                                bg-white bg-opacity-95 hover:bg-opacity-100
                                transition-all duration-200
                                shadow-sm
                            `}
                            style={{
                                color: 'var(--colors-status-error)',
                            }}
                        >
                            <span>{t('liveBanner.joinNow')}</span>
                            <Icon
                                icon="ph:arrow-right-thin"
                                className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`}
                            />
                        </div>
                    ) : (
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                handleSetReminder(e, nextVideo);
                            }}
                            className={`
                                flex-shrink-0
                                inline-flex items-center gap-2
                                px-3 py-0.5 rounded-full
                                font-medium
                                transition-all duration-200
                                shadow-sm
                                ${hasReminder 
                                    ? 'bg-status-warning text-white hover:bg-status-warning/90' 
                                    : 'bg-white hover:bg-white/90 text-status-warning'
                                }
                            `}
                        >
                            <span>{t(hasReminder ? 'liveBanner.reminderSet' : 'liveBanner.setReminder')}</span>
                            <Icon
                                icon={hasReminder ? 'ph:bell-ringing-thin' : 'ph:bell-thin'}
                                className={`w-4 h-4 ${hasReminder ? 'animate-bounce' : ''}`}
                            />
                        </button>
                    )}
                </div>
            </Link>
        </div>
    );
};

export default LiveBanner; 